import { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  DocumentReference,
  DocumentSnapshot,
  FieldValue,
} from "@atgof-firebase/firebase";
import React from "react";
import { LanguageContext } from "../common/language";
import DetailPage from "../components/pages/DetailPage";
import PhotoStrip from "../components/PhotoStrip";
import ReferentSelector from "../components/ReferentSelector";
import { RootStackParamList } from "../types";
import PASheet from "../components/PASheet";
import { SheetCategory } from "../common/sheet";
import { ProjectContext } from "../data/projectContext";
import { PhraseKey, safePh } from "../common/phrases";
import { View } from "react-native";
import SceneInfo from "../components/SceneInfo";
import { UserContext } from "../data/userContext";
import LabelledAttr from "../common/LabelledAttr";
import { SheetSections } from "../components/SheetSections";
import {
  cardNumberAttrs,
  generateNextSlateAttrs,
  removeSlate,
  updateLatestSlate,
} from "../data/team";
import { referencePathForScene } from "../data/backend/downloader";
import { sceneLabel, toScene } from "../common/scene";
import { episodeForScene } from "../common/episode";
import { useDocumentReference } from "../data/firestore";
import _ from "lodash";
import { useDocument } from "../data/useDocument";
import { getDocumentReference } from "../common/firestore";
import SubjectSelector from "../components/SubjectSelector";
import { deleteDocument, undeleteDocument } from "../data/util";
import { DeleteItemFn } from "../components/pages/NavPage";
import { subjectKindForCategory } from "@atgof-common/subject";
import { useProject } from "../data/useProject";

function LabelledUserName({
  sheet,
  fieldName,
  fieldPhraseK,
}: {
  sheet: DocumentSnapshot;
  fieldPhraseK: PhraseKey;
  fieldName: string;
}) {
  const { ph } = React.useContext(LanguageContext);
  const userRef = sheet.get(fieldName) as DocumentReference | undefined;
  const user = useDocument(userRef);
  const displayName: string | undefined = user?.get("displayName");
  return (
    <LabelledAttr
      label={ph(fieldPhraseK) as string}
      small
      style={{ marginRight: 16, marginBottom: 16 }}
    >
      {(displayName?.length ? displayName : user?.get("email")) ?? ""}
    </LabelledAttr>
  );
}

function MetadataSection({ sheet }: { sheet: DocumentSnapshot }) {
  return (
    <View style={{ flexDirection: "row", margin: 4 }}>
      {[
        ["created-by", "createdBy"],
        ["last-modified", "lastModifiedBy"],
      ].map(([phraseKey, k]) => (
        <LabelledUserName
          key={k}
          sheet={sheet}
          fieldName={k}
          fieldPhraseK={phraseKey}
        />
      ))}
    </View>
  );
}

export default function SheetScreen({
  route,
}: NativeStackScreenProps<RootStackParamList, "sheet">) {
  const pathOrPromise = route.params.path;
  const isNew = route.params.isNew === "true";
  const { adminCategories } = React.useContext(ProjectContext);
  const { user } = React.useContext(UserContext);
  const { ph, language } = React.useContext(LanguageContext);
  const project = useDocument(useProject());
  const daysRatherThanEpisodes: boolean =
    project?.get("daysRatherThanEpisodes") ?? false;
  const noSlates: boolean = project?.get("noSlates") ?? false;
  const [sheetPath, setSheetPath] = React.useState(
    undefined as string | undefined,
  );
  React.useEffect(() => {
    if (pathOrPromise instanceof Promise) pathOrPromise.then(setSheetPath);
    else setSheetPath(pathOrPromise);
  }, [pathOrPromise]);
  const sheet = useDocument(useDocumentReference(sheetPath));
  const category = sheet?.get("category") as SheetCategory | undefined;
  const isPASheet = category === "pa";
  const slate: string | undefined = sheet?.get("pa:slate");
  const teamRef: DocumentReference | undefined = sheet?.get("team");
  const [scene, setReferent] = React.useState<DocumentSnapshot>();
  const handleTeamOrSceneChange = React.useCallback(
    async (
      sceneRef: DocumentReference | undefined,
      newTeamRef: DocumentReference | undefined,
    ) => {
      if (!isPASheet || newTeamRef?.path === teamRef?.path) return;
      await removeSlate(
        teamRef,
        sheet?.id,
        slate,
        noSlates ? sceneRef : undefined,
      );
      const attrs = await generateNextSlateAttrs(sceneRef, noSlates);
      sheet?.ref.update(
        [...cardNumberAttrs, "pa:slate", "pa:cameras"].reduce(
          (data, k) => ({ ...data, [k]: attrs[k] ?? FieldValue.delete() }),
          { team: newTeamRef ?? FieldValue.delete() },
        ),
      );
    },
    [isPASheet, teamRef?.path, slate, sheet],
  );
  const handleTeamChange = React.useCallback(
    (newTeamRef: DocumentReference | undefined) =>
      handleTeamOrSceneChange(scene?.ref, newTeamRef),
    [handleTeamOrSceneChange, scene?.ref.path],
  );
  const deleteSheet = React.useCallback<DeleteItemFn>(() => {
    const sheetRef = sheet?.ref;
    const userRef = user.ref;
    return sheetRef
      ? {
          promise: deleteDocument(sheetRef, userRef).then(() => {
            removeSlate(
              teamRef,
              sheetRef.id,
              slate,
              noSlates ? sheet.get("scene") : undefined,
            );
          }),
          undo: () => {
            undeleteDocument(sheetRef, userRef);
            updateLatestSlate(
              teamRef,
              slate,
              slate,
              sheetRef.id,
              noSlates ? sheet.get("scene") : undefined,
            );
          },
        }
      : {
          promise: Promise.resolve(),
          undo: undefined,
        };
  }, [sheet, teamRef?.path, slate, user.ref.path, noSlates]);
  const setScene = React.useCallback(
    async (ref: DocumentReference | undefined) => {
      const doc = await ref?.get();
      setReferent(doc);
      await handleTeamOrSceneChange(ref, doc?.get("team"));
      await sheet?.ref.update({
        scene: ref,
        episode: ref === null ? null : episodeForScene(ref),
        lastModifiedBy: user.ref,
      }); // TODO Error handling
    },
    [sheet?.ref.path, user.ref.path, handleTeamOrSceneChange],
  );
  const referentPath = (sheet?.get("scene") as DocumentReference | undefined)
    ?.path;
  const subjectKey = subjectKindForCategory(category);
  const sceneObj = React.useMemo(
    () => (scene ? toScene(scene) : undefined),
    [scene],
  );

  React.useEffect(() => {
    const ref = getDocumentReference(user, referentPath);
    if (!ref) {
      setReferent(undefined);
      return;
    }
    return ref.onSnapshot(setReferent);
  }, [user, referentPath]);

  // TODO When sheet/sheetRef isn't set

  const subject = subjectKey ? sheet?.get(subjectKey) : undefined;

  function onSubjectChange(v: string | undefined) {
    if (v === subject || !subjectKey) return;
    sheet?.ref.update({
      [subjectKey]: v ?? FieldValue.delete(),
    });
  }

  const titleParts = [
    subject,
    scene && sceneLabel(scene.ref.path),
    isPASheet && sheet?.get("pa:slate"),
  ].filter((x) => x);

  return (
    <DetailPage
      title={
        isNew && titleParts.length < 2
          ? ((safePh(language, "new-" + category + "-sheet") ||
              ph("new-sheet")) as string)
          : titleParts.join(" - ") || (ph("edit-sheet") as string)
      }
      deleteItem={deleteSheet}
      newSheetContext={{ sceneRef: sceneObj?.ref }}
    >
      {sheet && (
        <View style={{ paddingTop: 16 }}>
          {adminCategories.includes("admin") && (
            <MetadataSection sheet={sheet} />
          )}
          <View
            style={{
              flexDirection: "row",
              marginBottom: 16,
              flexWrap: "wrap",
              gap: 16,
            }}
          >
            <ReferentSelector
              kind="scene"
              referent={scene?.ref}
              onReferentChange={setScene}
              daysRatherThanEpisodes={daysRatherThanEpisodes}
            />
          </View>
          {subjectKey && sceneObj && (
            <View style={{ flexDirection: "row", marginBottom: 16 }}>
              <SubjectSelector
                kind={subjectKey}
                value={subject}
                onChange={onSubjectChange}
                scene={sceneObj}
              />
            </View>
          )}
          {sceneObj && (
            <SceneInfo
              style={{ marginBottom: 16 }}
              scene={sceneObj}
              sceneRefToEdit={isPASheet ? sceneObj.ref : undefined}
              handleTeamChange={isPASheet ? handleTeamChange : undefined}
            />
          )}
          {category && (
            <PhotoStrip
              sheetRef={sheet.ref}
              sheetCategory={category}
              referencePath={referencePathForScene(scene?.ref, sheet?.id)}
              style={{ marginBottom: 16 }}
            />
          )}
          {isPASheet ? (
            scene && <PASheet sheet={sheet} scene={scene} />
          ) : (
            <SheetSections doc={sheet} category={category} />
          )}
        </View>
      )}
    </DetailPage>
  );
}
